import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getApiUrl, normalizeLoginEmail } from '../../utils/authUtils';

const API = getApiUrl();
const SUCCESS_MESSAGE =
  'If an account exists, we sent password reset instructions.';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/forgot-password`,
        { email: normalizeLoginEmail(email) },
        { withCredentials: true }
      );
      setSubmitted(true);
      toast.success(SUCCESS_MESSAGE);
    } catch (error) {
      if (!error.response) {
        toast.error('Unable to reach the server. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Request failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Reset your password</h1>
          <p className="text-slate-600 mt-2">
            Enter your email and we&apos;ll send reset instructions if an account exists.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {submitted ? (
            <div className="text-center space-y-4">
              <p className="text-slate-700">{SUCCESS_MESSAGE}</p>
              <Link to="/login">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <IconInput
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={Mail}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send reset instructions'}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-700"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
